/**
 * External-publishing integrations (News Watch Studio → distribution).
 *
 * Credentials live in a dedicated DDB item, NEVER on the brand row (which is
 * returned wholesale to the client). Only the connection STATUS (below) is ever
 * sent to the frontend — the secret (WordPress application password) stays
 * server-side.
 */

/** Per-brand WordPress connection. One WP site per brand (an agency's client
 *  each has their own site). Uses WordPress Application Passwords (revocable,
 *  scoped — NOT the account password). */
export interface WordPressConnection {
  siteUrl: string;
  username: string;
  /** Application password — server-side only, never serialized to the client. */
  applicationPassword: string;
  connectedAt: string;
}

/** Client-safe view of the WP connection — no secret. */
export interface WordPressConnectionStatus {
  connected: boolean;
  siteUrl?: string;
  username?: string;
  connectedAt?: string;
}

/** A WordPress taxonomy term (category or tag) for the publish dialog. */
export interface WordPressTerm {
  id: number;
  name: string;
}

/** Result of publishing an article to WordPress. */
export interface WordPressPublishResult {
  postId: number;
  postUrl: string;
  status: 'draft' | 'publish';
}

// ============================================================
// Generic outbound webhooks (Studio content → any endpoint)
// ============================================================

/**
 * `notification.<type>` events (plan §13.1) — one per registry type whose scope
 * is not `broadcast` and which isn't email-only. Deliberately NOT derived from
 * NOTIFICATION_REGISTRY at runtime: the wire contract a subscriber depends on
 * must not silently change shape when the registry gains an entry. Adding a type
 * here is the explicit act of publishing it.
 *
 * Absent on purpose: `notification.traaker_news` / `notification.geo_news`
 * (broadcast, marketing content — §13.3) and `notification.trial_lifecycle`
 * (email-only 1:1 onboarding copy).
 */
export type NotificationWebhookEvent =
  | 'notification.global_report'
  | 'notification.score_report'
  | 'notification.competitor_news'
  | 'notification.score_degradation'
  | 'notification.tasks_recos'
  | 'notification.credit_alert'
  | 'notification.audit_failed';

export const NOTIFICATION_WEBHOOK_EVENTS: NotificationWebhookEvent[] = [
  'notification.global_report',
  'notification.score_report',
  'notification.competitor_news',
  'notification.score_degradation',
  'notification.tasks_recos',
  'notification.credit_alert',
  'notification.audit_failed',
];

export type WebhookEvent =
  | 'article.generated'
  | 'article.published'
  | 'article.dismissed'
  | NotificationWebhookEvent;

export const WEBHOOK_EVENTS: WebhookEvent[] = [
  'article.generated',
  'article.published',
  'article.dismissed',
  ...NOTIFICATION_WEBHOOK_EVENTS,
];

/**
 * What a webhook subscribes to when the caller doesn't specify (or supplies
 * an empty/invalid) `events` array. Excludes `notification.*` deliberately —
 * those are score/credit payloads a subscriber must opt into explicitly, not
 * something a bare `POST /webhooks` silently starts fanning out. The
 * frontend's own create-dialog default (`WebhooksIntegration.tsx`) mirrors
 * this by construction so the UI and a direct API call can never disagree.
 */
export const DEFAULT_WEBHOOK_EVENTS: WebhookEvent[] = WEBHOOK_EVENTS.filter((e) => !e.startsWith('notification.'));

/** Stored webhook config. The `secret` is server-side only — used to HMAC-sign
 *  payloads; NEVER returned to the client after creation. */
export interface WebhookConfig {
  id: string;
  url: string;
  secret: string;
  events: WebhookEvent[];
  active: boolean;
  createdAt: string;
}

/** Client-safe view — secret redacted to its last 4 chars. */
export interface WebhookConfigView {
  id: string;
  url: string;
  secretHint: string; // e.g. "••••ab12"
  events: WebhookEvent[];
  active: boolean;
  createdAt: string;
}

/** One delivery attempt, for the per-webhook delivery log. */
export interface WebhookDelivery {
  webhookId: string;
  event: WebhookEvent;
  status: 'success' | 'failed';
  responseCode?: number;
  attempts: number;
  error?: string;
  deliveredAt: string;
  /**
   * Which channel this attempt went out on. Absent means `'webhook'` (every row
   * written before Slack existed), so the deliveries view must default it rather
   * than treat absence as a distinct case.
   */
  channel?: 'webhook' | 'slack';
}

/**
 * Slack deliveries share the `WEBHOOK_DELIVERY#` log (§13.2) but have no
 * per-webhook config row, so they use this fixed pseudo-id as their partition
 * segment — keeping the existing `WEBHOOK_DELIVERY#<id>#<reverseTs>` SK shape
 * intact and making Slack's history queryable the same way.
 */
export const SLACK_DELIVERY_ID = 'slack';

/** A channel variant in a webhook payload (flattened, subscriber-friendly). */
export interface WebhookVariant {
  channel: string;
  kind: string;
  /** Plain text for single-string variants; JSON string for structured (thread/script). */
  text: string;
}

/**
 * The JSON body POSTed to a subscriber (signed via `X-Traaker-Signature`).
 *
 * Self-contained by design — carries the FULL article + all channel variants so
 * the subscriber's automation (Make/n8n/Zapier → Slack/Notion/CMS) can act with
 * no fetch-back and no extra auth. Payload is a few KB; HMAC-signed for authenticity.
 */
export interface WebhookPayload {
  event: 'article.generated' | 'article.published' | 'article.dismissed';
  tenantId: string;
  brandId: string;
  articleId: string;
  occurredAt: string;
  data: {
    title: string;
    subtitle?: string;
    /** Full article body (markdown). */
    articleMarkdown?: string;
    /** All generated channel variants (announcement, thread, captions…). */
    variants?: WebhookVariant[];
    targetGeoQuery?: string;
    topicTags?: string[];
    status?: string;
    publishedUrl?: string;
    channels?: string[];
    editorUrl?: string;
  };
}

/**
 * The JSON body POSTed for a `notification.<type>` event (plan §13.1).
 *
 * Same "no fetch-back" philosophy as WebhookPayload: `data` is the renderer's
 * own structured output (deltas / degradation series / credit balance — whatever
 * it already computed to build the HTML), never a pointer the subscriber has to
 * resolve. Renderers that produce no machine-useful data are not fanned out.
 */
export interface NotificationWebhookPayload {
  event: NotificationWebhookEvent;
  tenantId: string;
  brandId?: string;
  brandName?: string;
  /** The registry type, without the `notification.` prefix. */
  type: string;
  periodStart?: string;
  periodEnd?: string;
  occurredAt: string;
  data: Record<string, unknown>;
  /** The AI commentary, when the type has one (D8 types only). */
  aiSummary?: string;
  /** Deep link to the relevant app page. */
  appUrl?: string;
}
